import React, { useEffect, useState } from 'react';

import PrintTools from '@/components/admin/PrintTools';
import { LEGACY_THEME_CLASS, legacyGroundClass } from '@/components/admin/PrintToolsPage';
import TabButton from '@/components/admin/PrintTextComparison/TabButton';
import { SPAThemeScope } from '@/components/admin/SysadminSPA/shared/SPAThemeScope';
import cn from '@/utils/cn';

interface Props {
  analyzePath: string;
  lessonId: number;
  programId: number;
  // id of the server-rendered element holding the sections table, toggled from here.
  sectionsPanelId: string;
}

type Tab = 'sections' | 'print';

// site_width_cap keeps the tab bar at the classic page width when the print pane widens the page
// (_print_text_tools.scss), so toggling tabs doesn't shift anything above the pane.
const TAB_BAR = 'site_width_cap tw-font-tci tw-my-4 tw-flex tw-items-center tw-border-0 tw-border-b '
  + 'tw-border-solid tw-border-spa-border';

// Tab bar for the lesson sections page: switches between the ERB-rendered sections table and the
// Print Tools panel. Both stay in the DOM so the sortable table and analyze results survive
// switching; the resize dispatch lets a re-shown Froala editor re-measure itself.
const SectionsPageTabs = ({ analyzePath, lessonId, programId, sectionsPanelId }: Props) => {
  const [activeTab, setActiveTab] = useState<Tab>('sections');

  useEffect(() => {
    const panel = document.getElementById(sectionsPanelId);
    if (panel) panel.hidden = activeTab !== 'sections';
    // Print tools get the wide reflow layout (like the standalone page); the sections table keeps
    // the default 990px pin. Capped for this page in _print_text_tools.scss.
    document.getElementById('page_wrapper')?.classList.toggle('full_width', activeTab === 'print');
    if (activeTab === 'print') requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  }, [activeTab, sectionsPanelId]);

  return (
    <SPAThemeScope className={LEGACY_THEME_CLASS}>
      {/* The tabs and the tool are on the SPA tokens; this legacy (light) page pins the light set. */}
      <div className={cn(TAB_BAR, LEGACY_THEME_CLASS)}>
        <TabButton active={activeTab === 'sections'} onClick={() => setActiveTab('sections')}>
          Sections
        </TabButton>
        <TabButton active={activeTab === 'print'} onClick={() => setActiveTab('print')}>
          Print Tools
        </TabButton>
      </div>
      <div className={cn(legacyGroundClass, activeTab !== 'print' && 'tw-hidden')}>
        <PrintTools
          analyzePath={analyzePath}
          lessonId={lessonId}
          programId={programId}
          workspaceLayout="split"
          onFileAccepted={() => setActiveTab('print')}
        />
      </div>
    </SPAThemeScope>
  );
};

export default SectionsPageTabs;
